import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { ScoreStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { AddToWatchlistIcon } from '@sorare/core/src/atoms/icons/AddToWatchlistIcon';
import { iconAngleLeft } from '@sorare/core/src/atoms/icons/system/iconAngleLeft.ts';
import { iconCart } from '@sorare/core/src/atoms/icons/system/iconCart.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { RewardMultiplier } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardMultiplier';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isActiveAppearance } from '@sorare/core/src/lib/players';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import {
  borderGradientStyle,
  hideScrollbar,
} from '@sorare/core/src/style/utils';
import { AddToWatchlistButton } from '@sorare/marketplace/src/components/watchlists/AddToWatchlistButton';
import { isGameStarted } from '@sorare/marketplace/src/shared/game/lib';
import { PlayerProperties } from '@sorare/marketplace/src/shared/player/PlayerDetails/PlayerProperties';
import { BriefGame } from '@sorare/marketplace/src/shared/stats/BriefGame';
import { LazyFixtureChart } from '@sorare/marketplace/src/shared/stats/LastScores/LazyFixtureChart';
import { PlayerGameScoreStats } from '@sorare/marketplace/src/shared/stats/PlayerGameScoreStats';

import { CompactSo5Lineup } from '#src/components/lineup/CompactSo5Lineup/index.tsx';
import { ContenderFooter } from '#src/components/lineup/Contender/ContenderFooter/index.tsx';
import { ContenderLiveProjectedRanking } from '#src/components/lineup/Contender/ContenderLiveProjectedRanking/index.tsx';
import { LineupHeader } from '#src/components/lineup/LineupHeader/index.tsx';

import { PlayerDetailsHeaderStats } from './PlayerDetailsHeaderStats/index.tsx';
import type {
  PlayerScoreDetailsViewQuery,
  PlayerScoreDetailsViewQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(FullWidthContainer)`
  --vertical-container-padding: var(--triple-unit);
  isolation: isolate;
  position: relative;
  background-color: var(--c-black);
  color: var(--c-white);
  width: var(--100vw);
  flex: 1;
  ${hideScrollbar}

  @media ${tabletAndAbove} {
    width: 492px;
    &.right {
      border-left: 1px solid var(--c-nd-150);
    }
    &.left {
      border-right: 1px solid var(--c-nd-150);
    }
  }
`;

const Header = styled(SBHorizontal)`
  flex-grow: 1;
  padding: var(--double-unit) var(--container-padding);
  border-bottom: 1px solid var(--c-nd-150);

  .right & {
    flex-direction: row-reverse;
  }
`;

const Blocks = styled(Vertical).attrs({ gap: 0 })`
  background: var(--c-black);
  & > * {
    padding-top: var(--vertical-container-padding);
    padding-bottom: var(--vertical-container-padding);
  }
  & > * + * {
    border-top: 1px solid var(--c-nd-150);
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
`;

const DetailsRoot = styled(Vertical).attrs({ gap: 1.5 })`
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  padding: var(--unit);
  backdrop-filter: blur(16px);

  &.golden {
    ${borderGradientStyle}
  }
`;

const WatchlistContainer = styled(FullWidthContainer)`
  padding-bottom: var(--vertical-container-padding);
`;

const Separator = styled.div`
  &:first-child,
  &:last-child {
    display: none;
  }
  width: 1px;
  height: var(--double-unit);
  margin: -2px 0; /* overflow the line-height of 12px */
  background-color: var(--c-nd-150);
`;

const BottomBorder = styled.hr`
  margin: 0;
  padding: 0;
  border: 1px solid var(--c-nd-150);
`;

const MATCH_VIEW_PLAYER_SCORE_QUERY = gql`
  query PlayerScoreDetailsViewQuery($playerSlug: String!, $gameId: ID!) {
    anyGame(id: $gameId) {
      id
      status: statusTyped
      mySo5Lineups {
        id
        so5Fixture {
          slug
        }
        so5Appearances(includeSubs: true) {
          substitutionState
          id
          anyCard {
            slug
            anyPlayer {
              slug
            }
          }
        }
        so5LeaderboardContender {
          slug
          so5Lineup {
            id
            so5Rankings {
              id
              ...ContenderLiveFooter_so5Ranking
            }
            ...CompactSo5Lineup_so5Lineup
            ...RewardMultiplier_lineup
          }
          ...LobbyLineupHeader_so5LeaderboardContender
          ...ContenderFooter_so5LeaderboardContender
        }
      }
      playerGameScore(playerSlug: $playerSlug) {
        id
        scoreStatus
        anyPlayer {
          slug
          displayName
          avatarPictureUrl
          commonPlayer(rarity: common) {
            id
          }
          anyCards(first: 1, ownedByMe: true) {
            totalCount
          }
          ...AddToWatchlistButton_anyPlayer
          ...PlayerProperties_anyPlayer
          ...PlayerDetailsHeaderStats_anyPlayerInterface
        }
        anyPlayerGameStats {
          id
          anyTeam {
            slug
            ...BriefGame_teamInterface
          }
        }
        ...PlayerGameScoreStats_playerGameScoreInterface
        ...PlayerDetailsHeaderStats_playerGameScoreInterface
      }
      ...BriefGame_anyGameInterface
    }
  }
  ${PlayerGameScoreStats.fragments.playerGameScoreInterface}
  ${BriefGame.fragments.anyGameInterface}
  ${BriefGame.fragments.teamInterface}
  ${AddToWatchlistButton.fragments.anyPlayer}
  ${PlayerProperties.fragments.anyPlayer}
  ${LineupHeader.fragments.so5LeaderboardContender}
  ${ContenderFooter.fragments.so5LeaderboardContender}
  ${ContenderLiveProjectedRanking.fragments.so5Ranking}
  ${RewardMultiplier.fragments.lineup}
  ${CompactSo5Lineup.fragments.so5Lineup}
  ${PlayerDetailsHeaderStats.fragments.anyPlayerInterface}
  ${PlayerDetailsHeaderStats.fragments.playerGameScoreInterface}
` as TypedDocumentNode<
  PlayerScoreDetailsViewQuery,
  PlayerScoreDetailsViewQueryVariables
>;

type Props = {
  playerSlug: string;
  gameId: string;
  onClose: () => void;
  side?: 'left' | 'right';
};

export const PlayerScoreDetailsView = ({
  playerSlug,
  gameId,
  onClose,
  side = 'left',
}: Props) => {
  const isDesktop = useIsDesktopAndAbove();
  const { generateSportContextPath } = useSportContext();
  const { data, loading } = useQuery(MATCH_VIEW_PLAYER_SCORE_QUERY, {
    variables: {
      playerSlug,
      gameId: `Game:${idFromObject(gameId)}`,
    },
  });

  if (loading) {
    return (
      <Root>
        <LoaderContainer>
          <LoadingIndicator small />
        </LoaderContainer>
      </Root>
    );
  }

  const { anyGame } = data || {};
  const { playerGameScore } = anyGame || {};

  if (!anyGame || !playerGameScore) {
    return null;
  }

  const { anyPlayer, anyPlayerGameStats } = playerGameScore;
  const { commonPlayer, anyCards } = anyPlayer;

  const so5Appearances = anyGame.mySo5Lineups.filter(so5Lineup => {
    return so5Lineup.so5Appearances
      .filter(isActiveAppearance)
      .some(so5Appearance => {
        return so5Appearance.anyCard?.anyPlayer.slug === anyPlayer.slug;
      });
  });

  const contenders = so5Appearances.map(so5Appearance => {
    return so5Appearance.so5LeaderboardContender;
  });
  const owned = anyCards.totalCount > 0;

  return (
    <Root key={anyPlayer.slug} className={classNames(side)}>
      <FullWidthContainer.NoPadding>
        <ContentWithStickyHeader
          header={
            <>
              <Header>
                <BodyM as="p" bold style={{ visibility: 'hidden' }}>
                  <FormattedMessage {...fantasy.playerPerformance} />
                </BodyM>
                <IconButton
                  color="transparent"
                  size="small"
                  icon={isDesktop ? iconAngleLeft : iconX}
                  onClick={onClose}
                  style={{
                    transform: side === 'right' ? 'rotate(180deg)' : 'none',
                  }}
                />
              </Header>
              <FullWidthContainer>
                <Blocks>
                  <Vertical gap={3}>
                    <PlayerProperties
                      usePlayerPicture
                      usePlayerAvailability={false}
                      useCardPositions
                      player={anyPlayer}
                    />
                    <PlayerDetailsHeaderStats
                      anyPlayer={anyPlayer}
                      eligiblePlayerGameScores={[playerGameScore]}
                    />
                  </Vertical>
                </Blocks>
              </FullWidthContainer>
            </>
          }
        >
          {!owned && !!commonPlayer && (
            <WatchlistContainer>
              <Horizontal>
                <Button
                  fullWidth
                  size="medium"
                  color="tertiary"
                  startIcon={<FontAwesomeIcon icon={iconCart} />}
                  to={generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_CARDS, {
                    params: {
                      slug: anyPlayer.slug,
                    },
                  })}
                >
                  <FormattedMessage defaultMessage="View Listing" />
                </Button>
                <AddToWatchlistButton
                  anyPlayer={anyPlayer}
                  size="medium"
                  color="secondary"
                  fullWidth
                  startIcon={<AddToWatchlistIcon />}
                  ctaLabel={
                    <FormattedMessage defaultMessage="Add to Watchlist" />
                  }
                />
              </Horizontal>
            </WatchlistContainer>
          )}
          <ContentWithStickyHeader
            header={
              <FullWidthContainer>
                <BottomBorder />
              </FullWidthContainer>
            }
          >
            <FullWidthContainer>
              <Blocks>
                <Vertical gap={3}>
                  <BriefGame game={anyGame} team={anyPlayerGameStats.anyTeam} />
                </Vertical>
                {!!owned && contenders.length > 0 && (
                  <Vertical gap={3}>
                    {contenders.map(so5LeaderboardContender => {
                      const so5Ranking =
                        so5LeaderboardContender.so5Lineup?.so5Rankings[0];
                      return (
                        <DetailsRoot
                          key={so5LeaderboardContender.slug}
                          className={classNames({
                            golden:
                              so5LeaderboardContender.so5Leaderboard
                                .roomWithBoostedRewards,
                          })}
                          style={
                            {
                              '--border-gradient': so5LeaderboardContender
                                .so5Leaderboard.roomWithBoostedRewards
                                ? 'var(--c-gradient-gold)'
                                : 'var(--c-nd-150)',
                            } as CSSProperties
                          }
                        >
                          <LineupHeader
                            showLineupsCount
                            so5LeaderboardContender={so5LeaderboardContender}
                          />
                          {!!so5LeaderboardContender.so5Lineup && (
                            <CompactSo5Lineup
                              so5Lineup={so5LeaderboardContender.so5Lineup}
                            />
                          )}
                          <ContenderFooter
                            so5LeaderboardContender={so5LeaderboardContender}
                            showBonus={false}
                          />
                          <Horizontal
                            gap={1}
                            className="whitespace-nowrap"
                            center
                          >
                            <ContenderLiveProjectedRanking
                              so5Ranking={so5Ranking}
                            />
                            {!!so5LeaderboardContender.so5Lineup && (
                              <>
                                <Separator />
                                <RewardMultiplier
                                  lineup={so5LeaderboardContender.so5Lineup}
                                  variant={Variant.TEXT}
                                />
                              </>
                            )}
                          </Horizontal>
                        </DetailsRoot>
                      );
                    })}
                  </Vertical>
                )}
                {playerGameScore.scoreStatus !== ScoreStatus.DID_NOT_PLAY &&
                  isGameStarted(anyGame.status) && (
                    <Vertical gap={3}>
                      <PlayerGameScoreStats
                        playerGameScores={[playerGameScore]}
                      />
                    </Vertical>
                  )}
                <Vertical gap={1.5}>
                  <LazyFixtureChart playerSlug={anyPlayer.slug} />
                </Vertical>
              </Blocks>
            </FullWidthContainer>
          </ContentWithStickyHeader>
        </ContentWithStickyHeader>
      </FullWidthContainer.NoPadding>
    </Root>
  );
};
